import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Megaphone, Send, Trash2, Users, CheckCircle2, Clock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/app/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import AudiencePicker from "@/components/app/AudiencePicker";
import EmailEngagementStats from "@/components/app/EmailEngagementStats";
import api from "@/lib/api";
import { wallClockToUtcISO, formatInAppTz, tzLabel } from "@/lib/timezone";

const STATUS_BADGE = {
  draft:     { label: "טיוטה",     cls: "bg-stone-100 text-stone-600 border-stone-200" },
  scheduled: { label: "מתוזמן",   cls: "bg-amber-50 text-amber-700 border-amber-200" },
  sending:   { label: "בשליחה",   cls: "bg-blue-50 text-blue-700 border-blue-200" },
  completed: { label: "הושלם",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "בוטל",     cls: "bg-red-50 text-red-700 border-red-200" },
};

const DEFAULT_FORM = {
  name: "",
  template_id: "",
  audience_type: "all",
  audience_tags: [],
  exclude_tags: [],
  audience_list_ids: [],
  send_at: "",
};

export default function AdminBroadcasts() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, tpl, tg, s] = await Promise.all([
        api.get("/admin/broadcasts"),
        api.get("/admin/email/templates"),
        api.get("/admin/tags"),
        api.get("/admin/email/stats/summary"),
      ]);
      setItems(b.data);
      setTemplates(tpl.data.filter((t) => t.is_active));
      setTags(tg.data.map((t) => t.tag));
      setStats((s.data && s.data.by_broadcast) || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("שם חובה");
    if (!form.template_id) return toast.error("בחר Template");
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.send_at) payload.send_at = wallClockToUtcISO(payload.send_at);
      else delete payload.send_at;
      await api.post("/admin/broadcasts", payload);
      toast.success("Broadcast נוצר");
      setCreating(false);
      setForm(DEFAULT_FORM);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שגיאה");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async (bid, name) => {
    if (!window.confirm(`לשלוח את "${name}" עכשיו?`)) return;
    try {
      const { data } = await api.post(`/admin/broadcasts/${bid}/send-now`);
      toast.success(`נכנסו ${data.queued} לתור השליחה`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שגיאה");
    }
  };

  const remove = async (bid) => {
    if (!window.confirm("למחוק Broadcast זה?")) return;
    try {
      await api.delete(`/admin/broadcasts/${bid}`);
      toast.success("נמחק");
      load();
    } catch (err) {
      toast.error("שגיאה");
    }
  };

  return (
    <AdminLayout title="Broadcasts">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-stone-500">
          שליחה חד-פעמית לקהל מסונן לפי תיוגים. Broadcast מקבל <strong>עדיפות</strong> על-פני Drip באותו חלון זמן.
        </p>
        <Button onClick={() => setCreating(true)} data-testid="new-broadcast-btn" className="h-11 rounded-full bg-brand-charcoal hover:bg-stone-800 text-white">
          <Plus className="w-4 h-4 me-2" />Broadcast חדש
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-stone-500">טוען...</div>
      ) : items.length === 0 ? (
        <EmptyState onCreate={() => setCreating(true)} />
      ) : (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="broadcasts-table">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200 text-right">
              <tr>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">שם</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">סטטוס</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">קהל</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">שליחה</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">נשלח / נפתח / הוקלק</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((b) => {
                const badge = STATUS_BADGE[b.status] || STATUS_BADGE.draft;
                return (
                  <tr key={b.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`broadcast-${b.id}`}>
                    <td className="px-4 py-3 font-bold">{b.name}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-600">
                      <div>{b.audience_type === "all" ? "כל המנויים" : `${b.audience_type}: ${(b.audience_tags || []).join(", ")}`}</div>
                      {b.exclude_tags?.length > 0 && <div className="text-red-500">חוץ מ: {b.exclude_tags.join(", ")}</div>}
                      {b.audience_list_ids?.length > 0 && (
                        <div className="text-brand-amber flex flex-wrap gap-1 mt-0.5">
                          <span className="text-stone-500">רשימות:</span>
                          {b.audience_list_ids.map((lid) => (
                            <span key={lid} className="ltr font-mono bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 text-[10px]">{lid}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-500 whitespace-nowrap">
                      {b.triggered_at ? formatInAppTz(b.triggered_at) :
                       b.send_at ? formatInAppTz(b.send_at) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <EmailEngagementStats stats={stats[b.id]} compact testId={`broadcast-stats-${b.id}`} />
                      {(b.failed > 0 || b.skipped > 0) && (
                        <div className="text-[10px] text-stone-400 mt-1">
                          {b.failed > 0 && <span className="text-red-500">{b.failed} נכשלו</span>}
                          {b.failed > 0 && b.skipped > 0 && " · "}
                          {b.skipped > 0 && <span>{b.skipped} דולגו</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        {["draft", "scheduled"].includes(b.status) && (
                          <button onClick={() => sendNow(b.id, b.name)} data-testid={`broadcast-send-${b.id}`} className="p-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700" title="שלח עכשיו">
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                        {b.status !== "sending" && (
                          <button onClick={() => remove(b.id)} data-testid={`broadcast-del-${b.id}`} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" title="מחק">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle>Broadcast חדש</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <Label className="text-sm font-bold mb-2 block">שם פנימי</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="למשל: ניוזלטר פברואר 2026" data-testid="broadcast-name-input" className="h-11" />
            </div>
            <div>
              <Label className="text-sm font-bold mb-2 block">Template לשליחה</Label>
              <Select value={form.template_id} onValueChange={(v) => setForm((f) => ({ ...f, template_id: v }))}>
                <SelectTrigger className="h-11" data-testid="broadcast-template-select"><SelectValue placeholder="בחר..." /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <AudiencePicker
              value={{ audience_type: form.audience_type, audience_tags: form.audience_tags, exclude_tags: form.exclude_tags, audience_list_ids: form.audience_list_ids }}
              onChange={(v) => setForm((f) => ({ ...f, ...v }))}
              suggestions={tags}
              testIdPrefix="broadcast-audience"
            />
            <div>
              <Label className="text-sm font-bold mb-2 block">
                מועד שליחה
                <span className="text-xs text-stone-500 font-normal"> (ריק = טיוטה)</span>
              </Label>
              <Input type="datetime-local" value={form.send_at} onChange={(e) => setForm((f) => ({ ...f, send_at: e.target.value }))} className="h-11" data-testid="broadcast-send-at-input" />
              <p className="text-xs text-stone-500 mt-1">
                השעה מתפרשת ב-<span className="font-bold text-brand-amber">{tzLabel()}</span>. אם הזמן בעבר / ריק → יישאר טיוטה עד לחיצה על &quot;שלח עכשיו&quot;.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>ביטול</Button>
            <Button onClick={save} disabled={saving} data-testid="broadcast-save-btn" className="bg-brand-charcoal text-white">
              {saving ? "יוצר..." : "צור Broadcast"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="bg-white rounded-2xl p-12 border border-dashed border-stone-300 text-center" data-testid="broadcasts-empty">
      <Megaphone className="w-12 h-12 text-stone-300 mx-auto mb-4" />
      <h3 className="font-[800] mb-2">אין עדיין Broadcasts</h3>
      <p className="text-sm text-stone-500 mb-6 max-w-md mx-auto">
        Broadcast הוא מייל חד-פעמי המיועד לקהל ממוקד. השתמש בו לניוזלטר, הודעה חשובה או קמפיין נקודתי. Broadcasts מקבלים עדיפות על-פני Drip פעיל.
      </p>
      <Button onClick={onCreate} className="h-11 rounded-full bg-brand-charcoal text-white">
        <Plus className="w-4 h-4 me-2" />Broadcast ראשון
      </Button>
    </div>
  );
}
